import { Box, SelectField, Text } from '@factorialco/deprecated-design-system'

import {
  CheckSubscriptionItemsInstalledDocument,
  NextInvoiceDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import PriceInfo from 'modules/upsell/components/SelfUpsell/UpgradePlanModal/PriceInfo'
import type Plan from 'modules/upsell/lib/selfUpsell/plans/Plan'
import {
  getDefaultVariant,
  getOneInstallationInfo,
} from 'modules/upsell/lib/selfUpsell/plans/utils'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'

type OneVariantSelectorProps = {
  addon: Plan
  crossSellingAddon?: Plan | undefined
  showPrice: boolean
  setSelectedAddonId: (id: string) => void
  installedAddonInternalId?: string | null
}

const OneVariantSelector = ({
  addon,
  crossSellingAddon,
  showPrice,
  setSelectedAddonId,
  installedAddonInternalId,
}: OneVariantSelectorProps) => {
  const {
    allAddons,
    setConfigPlan,
    period,
    togglePlanSelect,
    currency,
    discount,
    seats,
    currentSeats,
  } = useSelfUpsellContext()

  const { company } = useCurrentSession()

  const onePlans = allAddons.f25Addons

  const { data: subscriptionItemsData } = useQuery(CheckSubscriptionItemsInstalledDocument, {
    variables: {},
  })

  const { data: nextInvoiceData } = useQuery(NextInvoiceDocument, {
    variables: { companyId: company.id },
  })

  const oneInstallationInfo = getOneInstallationInfo({
    onePlans,
    installedAddonInternalId: installedAddonInternalId ?? null,
    period,
    subscriptionItemsData,
    nextInvoiceData,
  })

  const {
    allInstalledPlans: installedOnePlans,
    maxInstalledPlan: maxInstalledOnePlan,
    selectablePlans: selectableOnePlans,
    nextBillingDate,
  } = oneInstallationInfo

  const getPlanLabel = (plan: Plan): { label: string; disabled: boolean } => {
    const isInstalled = installedOnePlans.some(
      (installed) => installed.internalTitle === plan.internalTitle
    )
    if (!isInstalled) return { label: plan.title, disabled: false }

    const isMaxPlan = maxInstalledOnePlan?.internalTitle === plan.internalTitle
    const hasMultipleInstalled = installedOnePlans.length > 1

    if (hasMultipleInstalled && !isMaxPlan) {
      const untilLabel = nextBillingDate
        ? i18n.t('self_upsell.first_step.in_plan_until', {
            defaultValue: 'In your plan until {{date}}',
            date: nextBillingDate,
          })
        : i18n.t('marketplace.revamp.in_your_plan')
      return { label: `${plan.title} (${untilLabel})`, disabled: true }
    }

    return {
      label: `${plan.title} (${i18n.t('marketplace.revamp.in_your_plan')})`,
      disabled: true,
    }
  }

  const oneSelectorOptions = [
    ...onePlans.map((plan) => {
      const { label, disabled } = getPlanLabel(plan)
      return { label, value: plan.internalTitle, disabled }
    }),
  ]

  const handleOnePlanChange = (selectedPlan: Plan) => {
    const defaultVariant = getDefaultVariant(selectedPlan)
    if (!defaultVariant) return

    if (!selectedPlan.isSelected) togglePlanSelect(selectedPlan)

    selectedPlan.setActiveVariant(defaultVariant)
    setSelectedAddonId(selectedPlan.internalTitle)

    const addons = [selectedPlan.pricing.getPlanId(period) || '']
    const crossSellingPlanId = crossSellingAddon?.pricing.getPlanId(period)
    if (crossSellingPlanId) addons.push(crossSellingPlanId)

    setConfigPlan({
      addon: addons,
      variant: defaultVariant,
    })
  }

  useEffect(() => {
    if (selectableOnePlans.length === 0) return

    const currentPlanIsInstalled = installedOnePlans.some(
      (installed) => installed.internalTitle === addon.internalTitle
    )
    if (currentPlanIsInstalled && selectableOnePlans[0]) {
      handleOnePlanChange(selectableOnePlans[0])
    }
  }, [])

  const selectedOneVariant = getDefaultVariant(addon)

  return (
    <Box gap="s6" width="full" style={{ minWidth: '0' }}>
      <Text.Normal size="100" color="grey700" weight="medium">
        {i18n.t('growth.self_upsell.one_selector')}
      </Text.Normal>
      <Box gap="s8" width="full" style={{ minWidth: '0' }}>
        {oneSelectorOptions.length > 0 && (
          <Box width="full" style={{ minWidth: '0', maxWidth: '100%' }}>
            <SelectField
              hideLabel
              options={oneSelectorOptions}
              value={addon.internalTitle}
              onChange={(selectedId) => {
                const selectedPlan = onePlans.find((plan) => plan.internalTitle === selectedId)
                if (
                  installedOnePlans.some(
                    (installed) => installed.internalTitle === selectedPlan?.internalTitle
                  )
                )
                  return
                if (!selectedPlan) return
                handleOnePlanChange(selectedPlan)
              }}
              selectValue={addon.internalTitle}
              missingLabel={i18n.t('self_upsell.first_step.select_plan_type')}
            />
          </Box>
        )}

        {selectedOneVariant ? (
          <Box
            border={{
              all: {
                width: 's1',
                style: 'solid',
                color: 'viridian1000',
              },
            }}
            borderRadius={{ all: 'abs016' }}
            background={'viridian100'}
            padding="s16"
            gap="s8"
            justifyContent="spaceBetween"
            style={{
              height: '88px',
            }}
          >
            <Box flexDirection="row" justifyContent="spaceBetween" alignItems="flexStart">
              <Text.Normal size="200" weight="semibold" color="grey1000">
                {addon.title}
              </Text.Normal>
            </Box>
            {showPrice && (
              <PriceInfo
                variant={addon.getSpecificVariant(selectedOneVariant)}
                period={period}
                currency={currency}
                seats={seats}
                currentSeats={currentSeats}
                discount={discount}
                disabled={false}
              />
            )}
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}

export default OneVariantSelector
